// Pre-hidrata la caché HTTP del Service Worker con los GET clave de los
// módulos que el usuario pidió como críticos para modo offline:
//   • Mi turno       (My Work / operations)
//   • Recursos Humanos (HR)
//   • Mantenimiento  (Diesel maintenance)
//   • SGSSO / HSE
//
// Estrategia: al iniciar sesión (o al recuperar la conexión), disparamos
// estos GET en background. El SW los guarda en el API_CACHE con política
// network-first + fallback offline, así al perder señal las listas siguen
// visibles.
//
// Es idempotente y silencioso: cualquier fallo individual se traga (mejor
// esfuerzo). Corre en concurrencia controlada para no saturar la red.

// Endpoint that includes today's date computed at call time.
function todayIso() { return new Date().toISOString().slice(0, 10); }
function isoDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

const PREWARM_ENDPOINTS = [
  "/hr/employees",
  "/hr/employees?active=true",
  "/hr/attendance/last",
  "/hr/certifications/expiring?days_ahead=90",
  "/hr/requests/pending-count",
  "/hr/vacancies",
  "/hr/kpis",
  "/hr/kpis/absent-week",
  "/hr/reimbursements",
  "/hr/pdf-signatures?limit=500",
  "/hr/attendance/signatures?days=90",
  "/hr/attendance/blackout",
  // --- Mantenimiento Diésel ---
  "/maintenance/equipment",
  "/maintenance/equipment/lite-public",
  "/maintenance/orders",
  "/maintenance/inspections",
  "/maintenance/kpis",
  "/maintenance/kpis?days=90",
  "/maintenance/inspections/kpis?days=90",
  "/maintenance/kpis/backlog-aging",
  "/maintenance/reports/weekly-program",
  // --- SGSSO / HSE ---
  "/hse/observations",
  "/hse/toolbox",
  "/hse/investigations",
  "/hse/actions",
  "/hse/jsa",
  "/hse/permits",
  "/hse/iper",
  "/hse/daily-reports",
  "/hse/hazards-catalog",
  "/hse/bowtie",
  "/hse/kpis",
  "/hse/kpis?days=90",
  // --- Reportes semanales / mensuales (histórico) ---
  "/weekly-report/history?limit=12",
  "/settings/selfie-rules",
  // --- Catálogos compartidos ---
  "/projects",
  "/users",
  "/settings/company",
  "/settings/safety",
  "/settings/theme",
  "/notifications?unread=false",
];

let prewarming = false;
let lastRunAt = 0;
const COOLDOWN_MS = 60_000; // no re-run in <60s

/**
 * Pre-warm de PDFs pesados (semanal/mensual, últimos meses) para consulta
 * offline sin regenerar. Estos endpoints devuelven blob PDF — el SW los
 * cachea igual que cualquier GET /api/*. Corre en background separado del
 * prewarm principal porque los PDFs son grandes; usar `after: N ms` para
 * arrancar cuando el prewarm liviano ya terminó.
 */
async function prewarmPdfBlobs(axiosInstance, opts = {}) {
  if (typeof window === "undefined") return;
  if (!navigator.onLine) return;
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const monthStr = `${yyyy}-${mm}`;
  // Semana pasada (lunes)
  const ws = new Date(today);
  ws.setDate(ws.getDate() - ws.getDay() - 6); // domingo prev semana
  const weekStart = ws.toISOString().slice(0, 10);
  const pdfEndpoints = [
    `/hse/daily-reports/monthly/pdf?month=${monthStr}`,
    `/hse/daily-reports/weekly/pdf?start_date=${weekStart}`,
    `/hse/hazards-catalog/pdf`,
    `/maintenance/orders/weekly-schedule-pdf`,
    `/hr/attendance/audit-monthly-pdf?month=${monthStr}`,
  ];
  for (const path of pdfEndpoints) {
    try {
      await axiosInstance.get(path, { responseType: "blob", timeout: 20000 });
    } catch (_e) {
      // ignore — PDFs pueden fallar si el mes está vacío
    }
    // pequeña pausa entre PDFs para no saturar backend
    await new Promise((r) => setTimeout(r, 400));
  }
}

/**
 * Pre-carga en background las listas clave. Usa la instancia axios
 * inyectada para respetar interceptors (auth cookie + shift-owner).
 * @param {import("axios").AxiosInstance} axiosInstance
 * @param {object} opts { force?: boolean, concurrency?: number, onProgress?: fn }
 */
export async function prewarmCache(axiosInstance, opts = {}) {
  if (typeof window === "undefined") return { ok: false, reason: "no-window" };
  if (!navigator.onLine) return { ok: false, reason: "offline" };
  if (prewarming) return { ok: false, reason: "busy" };
  const now = Date.now();
  if (!opts.force && now - lastRunAt < COOLDOWN_MS) {
    return { ok: false, reason: "cooldown" };
  }
  prewarming = true;
  lastRunAt = now;

  const endpoints = [
    ...PREWARM_ENDPOINTS,
    `/hr/attendance?date=${todayIso()}`,
    // Iter102: calendario de 30 días (Calendario RH y captura de asistencia offline)
    `/hr/schedule?start_date=${todayIso()}&days=30`,
    // Iter102b: endpoints Operations que exigen date_from/date_to (30 días atrás)
    `/operations/summary?date_from=${isoDaysAgo(30)}&date_to=${todayIso()}`,
    `/operations/kpis?date_from=${isoDaysAgo(90)}&date_to=${todayIso()}`,
    `/operations/kpis/cycle-time?date_from=${isoDaysAgo(90)}&date_to=${todayIso()}`,
  ];
  const concurrency = Math.max(1, Math.min(opts.concurrency || 4, 8));
  let done = 0;
  let ok = 0;
  let failed = 0;

  const worker = async (list) => {
    while (list.length > 0) {
      const path = list.shift();
      try {
        await axiosInstance.get(path, { timeout: 8000 });
        ok += 1;
      } catch (_err) {
        // Silencioso — 401/403/404 son válidos para roles sin permiso
        failed += 1;
      } finally {
        done += 1;
        if (opts.onProgress) {
          try { opts.onProgress({ done, total: endpoints.length, ok, failed }); }
          catch (_e) { /* noop */ }
        }
      }
    }
  };

  const queue = [...endpoints];
  const workers = Array.from({ length: concurrency }, () => worker(queue));
  try {
    await Promise.all(workers);
  } finally {
    prewarming = false;
  }
  // PDFs pesados en background, no bloquean
  if (!opts.skipPdfs) {
    setTimeout(() => prewarmPdfBlobs(axiosInstance).catch(() => {}), 3000);
  }
  return { ok: true, cached: ok, failed, total: endpoints.length };
}

/**
 * Auto-runs the prewarm on connectivity recovery. Idempotent.
 */
let onlineBound = false;
export function initPrewarmOnReconnect(axiosInstance) {
  if (typeof window === "undefined" || onlineBound) return;
  onlineBound = true;
  window.addEventListener("online", () => {
    // Give the network a moment to settle before we pound it
    setTimeout(() => prewarmCache(axiosInstance).catch(() => {}), 1500);
  });
}
